import { memo, useCallback, useState } from 'react'

import { Save, Warning } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import { EventsEventIdAdPodsPostRequest, ResponseError } from '@nativewaves/nw-cloud-sdk/smep'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation, Trans } from 'react-i18next'

import { ConfirmButton } from '@shared/components/Confirm/ConfirmButton'
import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import { DialogFormContent } from '@shared/layouts'
import { DurationField } from 'components/DurationField'
import { SmepAPI } from 'services/api'
import { eventQueryKeys } from 'services/queryKeys'

type FormStructure = {
  duration: number
}

type NewAdPodDialogContentProps = {
  eventId: string
}

const predefinedValues = {
  '30sec': 30,
  '2min': 120,
  '5min': 300,
  '10min': 600,
  '15min': 900,
}

const STARTING_VALUE = predefinedValues['30sec']

// in seconds
const MIN_VALUE = 1
const MAX_VALUE = 3600

const NewAdPodDialogContentRaw = ({ eventId }: NewAdPodDialogContentProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])
  const { enqueueSnackbar } = useSnackbar()
  const handleClose = useCloseHandler()

  const [predefinedValue, setPredefinedValue] = useState<number | undefined>(STARTING_VALUE)

  const queryClient = useQueryClient()

  const { mutate: createEventAdPod } = useMutation({
    mutationFn: (params: EventsEventIdAdPodsPostRequest) => SmepAPI.events.eventsEventIdAdPodsPost(params),
    onSettled: (data, error) => {
      if (!error && !!data) {
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: eventQueryKeys.detail({ eventId }).queryKey }),
          queryClient.invalidateQueries({ queryKey: eventQueryKeys.adPods({ eventId }).queryKey }),
        ])
      }
    },
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
  })

  const { handleSubmit, control, setValue, watch } = useForm<FormStructure>({
    defaultValues: {
      duration: STARTING_VALUE,
    },
    mode: 'onChange',
  })

  const duration = watch('duration')

  const handleFormSubmit = useCallback(
    (formData: FormStructure) => {
      createEventAdPod(
        {
          eventId,
          smepEventAdPodInsert: {
            duration: formData.duration,
          },
        },
        {
          onSuccess: () => {
            handleClose()
            enqueueSnackbar(t('domain:Workspace.Event.adCreateSuccess'), {
              variant: 'success',
            })
          },
          onError: async (e) => {
            const err = e as ResponseError
            const error = await err.response.json()
            return enqueueSnackbar(error.description, { variant: 'error' })
          },
        },
      )
    },
    [createEventAdPod, enqueueSnackbar, eventId, handleClose, t],
  )

  const handleClick = useCallback(
    (value: number) => {
      setValue('duration', value, { shouldValidate: true })
      setPredefinedValue(value)
    },
    [setValue],
  )

  return (
    <DialogFormContent
      Illustration={null}
      title={t('domain:Workspace.Event.adCreateTitle')}
      SaveButton={
        <ConfirmButton
          sx={{ ml: 'auto' }}
          startIcon={<Save />}
          variant="text"
          onConfirm={handleSubmit(handleFormSubmit)}
          staticLabel={t('domain:Workspace.Event.adCreateSaveButton')}
          disabled={!duration || duration < MIN_VALUE || duration > MAX_VALUE}
        />
      }
    >
      <Controller
        name="duration"
        control={control}
        rules={{
          validate: (value) =>
            !value || value < MIN_VALUE || value > MAX_VALUE ? t('domain:Workspace.Event.adDurationError') : true,
        }}
        render={({ field, fieldState }) => (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 2 }}>{t('domain:Workspace.Event.adDuration')}:</Typography>
              <DurationField
                defaultValue={field.value}
                onChange={(value) => {
                  setPredefinedValue(value)
                  field.onChange(value)
                }}
                size="small"
                noYears
                noDays
                hasError={!!fieldState.error}
              />
            </Box>
            <Typography sx={{ mt: 1, color: (theme) => theme.palette.error.main }}>
              {fieldState.error?.message}
            </Typography>
          </Box>
        )}
      />
      <Typography>{t('domain:Workspace.Event.selectPreset')}:</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 5 }}>
        <Button
          disabled={predefinedValue === predefinedValues['30sec']}
          onClick={() => handleClick(predefinedValues['30sec'])}
          variant="outlined"
        >
          30 sec
        </Button>
        <Button
          disabled={predefinedValue === predefinedValues['2min']}
          onClick={() => handleClick(predefinedValues['2min'])}
          variant="outlined"
        >
          2 min
        </Button>
        <Button
          disabled={predefinedValue === predefinedValues['5min']}
          onClick={() => handleClick(predefinedValues['5min'])}
          variant="outlined"
        >
          5 min
        </Button>
        <Button
          disabled={predefinedValue === predefinedValues['10min']}
          onClick={() => handleClick(predefinedValues['10min'])}
          variant="outlined"
        >
          10 min
        </Button>
        <Button
          disabled={predefinedValue === predefinedValues['15min']}
          onClick={() => handleClick(predefinedValues['15min'])}
          variant="outlined"
        >
          15 min
        </Button>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '450px' }}>
        <Warning color="warning" />
        <Typography>
          <Trans ns="domain" i18nKey="Workspace.Event.adCreateWarning" />
        </Typography>
      </Box>
    </DialogFormContent>
  )
}

export const NewAdPodDialogContent = memo(NewAdPodDialogContentRaw)
